import { useRef } from 'react'

import { OneDataCollection, useDataCollectionSource } from '@factorialco/f0-react/dist/experimental'
import { FileFilled, File } from '@factorialco/f0-react/icons/app'

import { getStatusVariant, renderDeadline, renderPerson } from 'modules/ats/lib/utils/vacancies'
import i18n from 'modules/core/lib/i18n'

export interface SuggestedPerson {
  id: string
  firstName: string
  lastName: string
  avatar?: { url: string } | null
}

export interface AutoAssignSuggestion {
  [key: string]: unknown
  id: string
  vacancyName: string
  status: string
  statusLabel: string
  suggestedRecruiter: SuggestedPerson | null
  suggestedRecruiterId: string | null
  suggestedHiringManager: SuggestedPerson | null
  suggestedHiringManagerId: string | null
  suggestedJobOpening: string | null
  suggestedJobPostingId: string | null
  lineManager: SuggestedPerson | null
  teamName: string | null
  locationName: string | null
  deadline: string | null
  vacancyType: string | null
  vacancyTypeLabel: string | null
  originType: string | null
  originTypeLabel: string | null
}

interface AutoAssignReviewTableProps {
  data: AutoAssignSuggestion[] | null
  onConfirm: (vacancyIds: string[]) => void
  onCancel: () => void
}

export const AutoAssignReviewTable = ({
  data,
  onConfirm,
  onCancel,
}: AutoAssignReviewTableProps) => {
  const didSelectAll = useRef(false)

  const noMatchAlertTag = {
    type: 'alertTag' as const,
    value: {
      label: i18n.t('ats.vacancies.auto_assign_review.no_match'),
      level: 'warning' as const,
    },
  }

  const source = useDataCollectionSource<AutoAssignSuggestion>(
    {
      dataAdapter: {
        fetchData: () => {
          if (data) return { records: data }
          return new Promise<{ records: AutoAssignSuggestion[] }>(() => {})
        },
      },
      allPagesSelection: true,
      selectable: (item: AutoAssignSuggestion) => item.id,
      bulkActions: () => ({
        primary: [
          {
            id: 'confirm',
            label: i18n.t('ats.vacancies.auto_assign_review.confirm'),
            keepSelection: true,
          },
        ],
        secondary: [
          {
            id: 'cancel',
            label: i18n.t('ats.vacancies.auto_assign_review.cancel'),
            keepSelection: true,
          },
        ],
      }),
    },
    [data]
  )

  return (
    <OneDataCollection
      source={source}
      onSelectItems={(selectedItems, _clearSelectedItems, handleSelectAllItems) => {
        if (!didSelectAll.current && handleSelectAllItems && selectedItems.itemsStatus.length > 0) {
          didSelectAll.current = true
          handleSelectAllItems(true)
          return
        }

        if (didSelectAll.current && selectedItems.selectedCount === 0) {
          onCancel()
        }
      }}
      onBulkAction={(action, selectedItems) => {
        if (action === 'confirm') {
          const ids = selectedItems.itemsStatus
            .filter((s) => s.checked)
            .map((s) => String(s.item.id))
          onConfirm(ids)
        }

        if (action === 'cancel') {
          onCancel()
        }
      }}
      visualizations={[
        {
          type: 'table',
          options: {
            frozenColumns: 1,
            columns: [
              {
                label: i18n.t('ats.vacancies.auto_assign_review.columns.vacancy_name'),
                render: (item: AutoAssignSuggestion) => item.vacancyName,
              },
              {
                label: i18n.t('ats.vacancies.auto_assign_review.columns.suggested_recruiter'),
                render: (item: AutoAssignSuggestion) =>
                  item.suggestedRecruiter ? renderPerson(item.suggestedRecruiter) : noMatchAlertTag,
              },
              {
                label: i18n.t('ats.vacancies.auto_assign_review.columns.suggested_hiring_manager'),
                render: (item: AutoAssignSuggestion) =>
                  item.suggestedHiringManager
                    ? renderPerson(item.suggestedHiringManager)
                    : noMatchAlertTag,
              },
              {
                label: i18n.t('ats.vacancies.table.columns.line_manager'),
                render: (item: AutoAssignSuggestion) => renderPerson(item.lineManager),
              },
              {
                label: i18n.t('ats.vacancies.auto_assign_review.columns.suggested_job_opening'),
                render: (item: AutoAssignSuggestion) =>
                  item.suggestedJobOpening ? item.suggestedJobOpening : noMatchAlertTag,
              },
              {
                label: i18n.t('ats.vacancies.auto_assign_review.columns.status'),
                render: (item: AutoAssignSuggestion) => {
                  const variant = getStatusVariant(item.status)
                  return variant
                    ? {
                        type: 'status' as const,
                        value: { label: item.statusLabel, status: variant },
                      }
                    : '-'
                },
              },
              {
                label: i18n.t('ats.vacancies.table.columns.team'),
                render: (item: AutoAssignSuggestion) =>
                  item.teamName ? { type: 'tag' as const, value: { label: item.teamName } } : '-',
              },
              {
                label: i18n.t('ats.vacancies.table.columns.market'),
                render: (item: AutoAssignSuggestion) =>
                  item.locationName
                    ? { type: 'tag' as const, value: { label: item.locationName } }
                    : '-',
              },
              {
                label: i18n.t('ats.vacancies.table.columns.deadline'),
                render: (item: AutoAssignSuggestion) => renderDeadline(item.deadline),
              },
              {
                label: i18n.t('ats.vacancies.table.columns.vacancy_type'),
                render: (item: AutoAssignSuggestion) => {
                  if (!item.vacancyType) return '-'
                  const color: 'flubber' | 'purple' =
                    item.vacancyType === 'new_position' ? 'flubber' : 'purple'
                  return {
                    type: 'dotTag' as const,
                    value: {
                      color,
                      label: item.vacancyTypeLabel ?? '',
                    },
                  }
                },
              },
              {
                label: i18n.t('ats.vacancies.table.columns.origin'),
                render: (item: AutoAssignSuggestion) =>
                  item.originType
                    ? {
                        type: 'tag' as const,
                        value: {
                          label: item.originTypeLabel ?? '',
                          icon: item.originType === 'requisition' ? FileFilled : File,
                        },
                      }
                    : '-',
              },
            ],
          },
        },
      ]}
      emptyStates={{
        'no-data': {
          emoji: '🤖',
          title: i18n.t('ats.vacancies.auto_assign_review.empty_state.title'),
          description: i18n.t('ats.vacancies.auto_assign_review.empty_state.description'),
        },
      }}
    />
  )
}
